import Dialog from '../../ui/modal/Dialog';
import React, {useMemo, useState} from 'react';
import {handleError, handleSuccess} from '../../utils/Apollo';
import {useMutation, useQuery} from '@apollo/client';
import gql from 'graphql-tag';
import {DateTime} from 'luxon';
import {Event, EventsFeed, ParentLicense, QueryEventsArgs, Trainer} from '../../schema';
import UICheckbox from '../../ui/form/UICheckbox';
import UIListBox from '../../ui/form/UIListBox';
import cn from 'classnames';
import Avatar from '../../ui/Avatar';
import IconInfo from "../icon/IconInfo";

type Props = {
  parentLicenseId: string
  show: boolean
  licensedTrainersIds: string[]
  setShowDialog: (close: boolean) => void
  refetchQueries?: string[]
  categoryName?: string
}

interface QueryFeed {
  trainers: Trainer[]
}

export default function AddSubLicenseDlg({
                                           parentLicenseId,
                                           licensedTrainersIds,
                                           show,
                                           setShowDialog,
                                           refetchQueries,
                                           categoryName
                                         }: Props) {
  const [addSubLicense] = useMutation(addSubLicenseMutation);
  const [trainerId, setTrainerId] = useState<string | undefined>(undefined);
  const [showError, setShowError] = useState(false);


  const {data, loading, error} = useQuery<QueryFeed>(query);

  const onAddLicense = () => {
    if (!trainerId) {
      setShowError(true);
      return;
    }
    addSubLicense({
      variables: {trainerId, parentLicenseId},
      refetchQueries: refetchQueries
    }).then(() => {
      handleSuccess(`License was added to trainer`);
      setShowDialog(false);
    }, (apolloError) => handleError(apolloError));
  }

  const handleTrainerSelection = (selectedEventId: string) => {
    setShowError(false);
    setTrainerId(selectedEventId);
  }

  return (
    <Dialog show={show} actionBtnTitle="Add trainer" onClose={() => setShowDialog(false)}
            title={`Add licensed trainer`} onConfirm={() => onAddLicense()} closeBtnTitle="Cancel">
      <>
        {loading && <div>Loading...</div>}
        {error && <div>{error.message}</div>}
        {data &&
          <>
            <div className="rounded-md bg-blue-light p-4 mb-3">
              <div className="flex">
                <div className="flex-shrink-0">
                  <IconInfo className="h-5 w-5 text-blue" aria-hidden="true"/>
                </div>
                <div className="ml-3 text-sm text-blue-hover">
                  If a trainer already has a personal {categoryName} license, it will be converted to a sub-license of
                  your company license with an identical expiration date.
                </div>
              </div>
            </div>
            <UIListBox value={trainerId} onChange={(value) => handleTrainerSelection(value)}
                       emptyLabel="Select trainer"
                       options={data.trainers.filter((x) => !licensedTrainersIds.includes(x.id)).map((trainer: Trainer) => ({
                         id: trainer.id,
                         value: trainer.id,
                         label: <div className="flex"><Avatar name={trainer.name}
                                                              className={'rounded-full w-6 h-6 mr-2'}
                                                              url={trainer.photo ? trainer.photo : undefined}/>{trainer.name}
                         </div>,
                         selected: false
                       }))}
            />
            <span className={cn('text-red text-sm', {'hidden': !showError})}>Select a trainer </span>
          </>
        }
      </>
    </Dialog>
  )
}

const query = gql`
  query GetTrainers {
    trainers: trainers(onlyActive: true) {
      id
      name
      photo
    },
  }
`

const addSubLicenseMutation = gql`
  mutation AddLicenseMutation($parentLicenseId: ID!, $trainerId: ID!) {
    addSubLicense(parentLicenseId: $parentLicenseId, trainerId: $trainerId) {
      id
    }
  }
`;
